import React from "react";
import {observer} from "mobx-react";
import {useInjection} from "inversify-react";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import {PlanExtensionCatalogEntity} from "@dropDesk/domain/entities/plan_extensions/plan_extension_catalog.entity";
import {
    PlanExtensionSimulateHireEntity
} from "@dropDesk/domain/entities/plan_extensions/plan_extension_simulate_hire.entity";
import {
    BackHeader,
    BenefitItem,
    BenefitText,
    BenefitsGrid,
    DetailItem,
    DetailLabel,
    DetailsGrid,
    DetailValue,
    Divider,
    ExtBadge,
    ExtDescription,
    ExtIconBadgeRow,
    ExtIconWrapper,
    ExtInfo,
    ExtName,
    RenewalNotice,
    RenewalNoticeText,
    TrialNotice,
    TrialNoticeSubtitle,
    TrialNoticeTextGroup,
    TrialNoticeTitle,
} from "./styled";
import {SectionCard, SectionTitle} from "./section_card";
import DropDeskIcon from "@dropDesk/storage/icons";
import {ICONS_DROPDESK} from "@dropDesk/storage/icons/icons.d";

const SLUG_ICON_MAP: Record<string, string> = {
    'internal_chat': ICONS_DROPDESK.internalChat,
    'use_app': ICONS_DROPDESK.mobile,
    'client_panel': ICONS_DROPDESK.browser,
};

const SLUG_BENEFITS: Record<string, string[]> = {
    'internal_chat': ['Chat entre atendentes', 'Histórico interno', 'Notificações em tempo real', 'Comunicação centralizada'],
    'use_app': ['Atenda de qualquer lugar', 'Acompanhe sua equipe', 'Notificações em tempo real', 'Gestão na palma da mão'],
    'client_panel': ['Acompanhamento de chamados', 'Histórico completo', 'Respostas do cliente', 'Base de conhecimento'],
};

type Props = {
    extension: PlanExtensionCatalogEntity;
    simulate: PlanExtensionSimulateHireEntity;
    onCancel: () => void;
}

const ExtensionConfirmationDetails = observer(({extension, simulate, onCancel}: Props) => {
    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;

    const icon = SLUG_ICON_MAP[extension.slug] ?? ICONS_DROPDESK.thunder;
    const badgeProps = extension.isCore
        ? {background: `${colors.success}18`, color: colors.success, label: 'Grátis', icon: null}
        : {background: `${colors.accent}18`, color: colors.accent, label: 'Premium', icon: ICONS_DROPDESK.subscription};

    const benefits = SLUG_BENEFITS[extension.slug] ?? [];

    return (
        <SectionCard background={'transparent'} border={colors.border}>

            <BackHeader color={colors.text} type="button" onClick={onCancel}>
                <DropDeskIcon icon={ICONS_DROPDESK.arrowLeft} size={18} color={colors.text}/>
                Confirmar Extensão
            </BackHeader>

            <Divider color={colors.border}/>

            {/* Header: ícone + badge + nome + descrição */}
            <ExtIconBadgeRow>
                <ExtIconWrapper background={`${colors.accent}12`} color={colors.accent}>
                    <DropDeskIcon icon={icon} size={55} color={colors.accent}/>
                </ExtIconWrapper>
                <ExtInfo>
                    <ExtBadge background={badgeProps.background} color={badgeProps.color}>
                        {badgeProps.icon && (
                            <DropDeskIcon icon={badgeProps.icon} size={11} color={badgeProps.color}/>
                        )}
                        {badgeProps.label}
                    </ExtBadge>
                    <ExtName color={colors.text}>{extension.name}</ExtName>
                    {extension.description && (
                        <ExtDescription color={colors.hint}>{extension.description}</ExtDescription>
                    )}
                </ExtInfo>
            </ExtIconBadgeRow>

            <RenewalNotice background={`${colors.accent}10`} color={colors.hint}>
                <DropDeskIcon icon={ICONS_DROPDESK.info} size={16} color={colors.accent}/>
                <RenewalNoticeText color={colors.text}>
                    A renovação do contrato da extensão é automática junto com a assinatura do seu plano.
                </RenewalNoticeText>
            </RenewalNotice>

            {simulate.trialDaysApplied > 0 && (
                <TrialNotice background={`${colors.success}10`} color={colors.success}>
                    <DropDeskIcon icon={ICONS_DROPDESK.check} size={16} color={colors.success}/>
                    <TrialNoticeTextGroup>
                        <TrialNoticeTitle color={colors.success}>
                            Teste grátis por {simulate.trialDaysApplied} dias
                        </TrialNoticeTitle>
                        <TrialNoticeSubtitle color={colors.text}>
                            Cancele grátis até {simulate.trialDaysApplied} dias de teste.
                        </TrialNoticeSubtitle>
                    </TrialNoticeTextGroup>
                </TrialNotice>
            )}
            {(extension.trialDays ?? 0) > 0 && simulate.trialDaysApplied === 0 && (
                <TrialNotice background={`${colors.warning}18`} color={colors.warning}>
                    <DropDeskIcon icon={ICONS_DROPDESK.warning} size={16} color={colors.warning}/>
                    <TrialNoticeTextGroup>
                        <TrialNoticeTitle color={colors.warning}>
                            Período de teste já utilizado
                        </TrialNoticeTitle>
                        <TrialNoticeSubtitle color={colors.text}>
                            Você já utilizou os {extension.trialDays} dias gratuitos desta extensão. A cobrança será
                            imediata.
                        </TrialNoticeSubtitle>
                    </TrialNoticeTextGroup>
                </TrialNotice>
            )}

            <Divider color={colors.border}/>

            {/* Detalhes */}
            <SectionTitle color={colors.text}>Detalhes da Extensão:</SectionTitle>
            <DetailsGrid>
                <DetailItem>
                    <DetailLabel color={colors.hint}>Valor mensal</DetailLabel>
                    <DetailValue color={colors.text}>
                        {extension.isCore
                            ? 'Grátis'
                            : `R$ ${extension.defaultPrice.toLocaleString('pt-BR', {minimumFractionDigits: 2})}`
                        }
                    </DetailValue>
                </DetailItem>

                <DetailItem>
                    <DetailLabel color={colors.hint}>Recorrência</DetailLabel>
                    <DetailValue color={colors.text}>Mensal</DetailValue>
                </DetailItem>

                <DetailItem>
                    <DetailLabel color={colors.hint}>Dias de teste</DetailLabel>
                    <DetailValue color={colors.text}>
                        {simulate.trialDaysApplied > 0 ? `${simulate.trialDaysApplied} dias` : '0 dias'}
                    </DetailValue>
                </DetailItem>
            </DetailsGrid>

            {benefits.length > 0 && (
                <>
                    <Divider color={colors.border}/>
                    <SectionTitle color={colors.text}>Benefícios:</SectionTitle>
                    <BenefitsGrid>
                        {benefits.map((benefit) => (
                            <BenefitItem key={benefit}>
                                <DropDeskIcon icon={ICONS_DROPDESK.check} size={13} color={colors.success}/>
                                <BenefitText color={colors.text}>{benefit}</BenefitText>
                            </BenefitItem>
                        ))}
                    </BenefitsGrid>
                </>
            )}

        </SectionCard>
    );
});

export default ExtensionConfirmationDetails;
